import type {ReactNode} from 'react';
import {
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Download,
  Gamepad2,
  Monitor,
  Users,
} from 'lucide-react';
import {EditorialAuthorLink} from './EditorialAuthor';

type GearsEDayBetaNewsPageProps = {
  header: ReactNode;
  footer: ReactNode;
};

const freePlayDays =
  'https://news.xbox.com/en-us/2026/08/13/template-free-play-days-08-13-2026/';
const xboxStoreBeta =
  'https://www.xbox.com/en-US/games/store/gears-of-war-e-day-multiplayer-beta/9nhlzs3g6d8h';
const xboxDirect =
  'https://news.xbox.com/en-us/2026/06/07/gears-of-war-eday-direct-xbox-games-showcase-2026/';
const gearsMultiplayer = 'https://www.gearsofwar.com/en-us/news/multiplayer/';

const ultimateOffers = [
  {
    badge: 'Ultimate · 1 mês',
    title: 'Game Pass Ultimate - 1 mês',
    description:
      'A opção mais prudente para jogar Gears of War: E-Day no lançamento e avaliar o catálogo sem assumir três meses de uma vez.',
    href: 'https://meli.la/2kfg1f8',
  },
  {
    badge: 'Ultimate · 3 meses',
    title: 'Game Pass Ultimate - 3 meses',
    description:
      'Faz sentido para quem já pretende continuar no multiplayer, explorar outros jogos do catálogo e usar o serviço durante o trimestre.',
    href: 'https://meli.la/2er2Wr7',
  },
];

export function GearsEDayBetaNewsPage({header, footer}: GearsEDayBetaNewsPageProps) {
  return (
    <main className="site-shell article-shell news-article-shell">
      {header}
      <article className="ps5-article news-article gears-eday-beta-news">
        <header className="article-hero">
          <div className="article-hero-copy">
            <a className="breadcrumb" href="/noticias">GuiaSite / Notícias / Games</a>
            <p className="section-kicker">Beta aberto · Xbox e PC</p>
            <h1>Beta aberto de Gears of War: E-Day começa hoje: como jogar</h1>
            <p className="article-lead">
              O teste público vai de 13 a 17 de agosto no Xbox Series X|S e PC. Veja o
              horário no Brasil, como baixar, quais modos estão disponíveis e quando o
              Game Pass realmente entra na decisão.
            </p>
            <div className="article-meta">
              <EditorialAuthorLink />
              <span><time dateTime="2026-08-13">Publicado em 13 de agosto de 2026</time></span>
              <span>Leitura útil: 8 min</span>
            </div>
          </div>
          <figure className="article-hero-media news-hero-media">
            <img
              loading="eager"
              src="/guisite/news-gears-eday-beta-aberto-2026-oficial.webp"
              alt="Marcus Fenix e Dom Santiago na arte oficial de Gears of War E-Day"
              width={1600}
              height={900}
              fetchPriority="high"
              decoding="async"
            />
            <figcaption>Imagem oficial: Xbox Game Studios / The Coalition.</figcaption>
          </figure>
        </header>

        <div className="article-layout">
          <aside className="article-toc" aria-label="Índice da notícia">
            <strong>Nesta notícia</strong>
            <a href="#resumo-gears-beta">Resposta direta</a>
            <a href="#datas-gears-beta">Datas e horários</a>
            <a href="#baixar-gears-beta">Como baixar</a>
            <a href="#modos-gears-beta">Modos disponíveis</a>
            <a href="#avaliar-gears-beta">O que avaliar</a>
            <a href="#game-pass-gears-beta">Game Pass e lançamento</a>
            <a href="#faq-gears-beta">Perguntas frequentes</a>
            <a href="#fontes-gears-beta">Fontes</a>
          </aside>

          <div className="article-body">
            <section className="answer-card" id="resumo-gears-beta">
              <p className="section-kicker">Resposta direta</p>
              <h2>O beta aberto de Gears of War: E-Day já pode ser jogado.</h2>
              <p>
                O segundo fim de semana do teste começou em <strong>13 de agosto, às 16h</strong>,
                e termina em <strong>17 de agosto, às 14h</strong>, pelo horário de Brasília. Ele
                está disponível no <strong>Xbox Series X|S e PC</strong>, com Horde Siege para 12
                jogadores e Versus 4v4.
              </p>
              <p>
                O aplicativo do beta é gratuito e este fim de semana não exige pré-venda. No
                console, porém, a Xbox informa que o multiplayer online requer um plano Game Pass
                com acesso ao jogo online. No PC, o teste pode ser instalado pelas lojas oficiais
                indicadas pela Xbox.
              </p>
              <div className="news-signal-row" aria-label="Informações principais">
                <span><CalendarDays size={18} /> 13 a 17 de agosto</span>
                <span><Gamepad2 size={18} /> Xbox Series X|S e PC</span>
                <span><Users size={18} /> PvE para 12 e PvP 4v4</span>
              </div>
            </section>

            <section id="datas-gears-beta">
              <p className="section-kicker">Janela oficial</p>
              <h2>Quando começa e termina o beta de Gears of War: E-Day?</h2>
              <div className="news-fact-table" role="table" aria-label="Datas e dados do beta de Gears of War E-Day">
                <div role="row"><strong>Informação</strong><strong>Confirmado</strong><strong>O que significa</strong></div>
                <div role="row"><span>Início</span><span>13 ago, 16h</span><span>Conversão de 12h PT para Brasília</span></div>
                <div role="row"><span>Encerramento</span><span>17 ago, 14h</span><span>Conversão de 10h PT para Brasília</span></div>
                <div role="row"><span>Plataformas</span><span>Xbox Series X|S e PC</span><span>Sem versão para Xbox One ou PS5</span></div>
                <div role="row"><span>Preço do aplicativo</span><span>Gratuito</span><span>Console exige acesso a multiplayer online</span></div>
                <div role="row"><span>Lançamento do jogo</span><span>6 out 2026</span><span>Dia um no Game Pass Ultimate</span></div>
              </div>
              <p className="source-note">
                Os horários em Brasília são conversões dos horários PT publicados pela Xbox. A
                própria empresa ressalva que datas e horários de beta podem mudar.
              </p>
            </section>

            <section id="baixar-gears-beta">
              <p className="section-kicker">Instalação</p>
              <h2>Como baixar o beta aberto no Xbox e no PC</h2>
              <div className="recommendation-grid news-pick-grid">
                <div>
                  <Download aria-hidden="true" />
                  <strong>No Xbox Series X|S</strong>
                  <p>
                    Abra a Microsoft Store, procure por “Gears of War: E-Day Multiplayer Beta” e
                    selecione a página publicada pela Xbox Game Studios. Também é possível acessar
                    a coleção Free Play Days pela área de assinaturas da loja.
                  </p>
                </div>
                <div>
                  <Monitor aria-hidden="true" />
                  <strong>No PC</strong>
                  <p>
                    Entre com sua conta Microsoft, abra a página oficial do beta e escolha a opção
                    de instalação disponível para PC. Confirme espaço livre e atualize o driver da
                    placa de vídeo antes da primeira partida.
                  </p>
                </div>
              </div>
              <a className="text-link" href={xboxStoreBeta} target="_blank" rel="noopener noreferrer">
                Abrir a página oficial do beta <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="modos-gears-beta">
              <h2>Quais modos estão disponíveis no beta?</h2>
              <p>
                O teste coloca lado a lado as duas frentes mais importantes do multiplayer. A
                primeira é <strong>Horde Siege</strong>, experiência PvE para 12 pessoas divididas
                em três esquadrões. A segunda é o <strong>Versus 4v4</strong>, voltado ao confronto
                competitivo e à leitura mais direta de movimentação, armas e rede.
              </p>
              <div className="news-fact-table" role="table" aria-label="Modos do beta de Gears of War E-Day">
                <div role="row"><strong>Modo</strong><strong>Formato</strong><strong>Melhor teste para</strong></div>
                <div role="row"><span>Horde Siege</span><span>PvE, 12 jogadores</span><span>Cooperação, escala e estabilidade com grupos maiores</span></div>
                <div role="row"><span>Versus</span><span>PvP, 4 contra 4</span><span>Resposta dos controles, combate e qualidade da conexão</span></div>
              </div>
              <p>
                A Xbox também informa que há desafios capazes de liberar recompensas exclusivas
                do beta. O objetivo declarado do teste é coletar feedback antes do lançamento em
                outubro, portanto quedas, ajustes de equilíbrio e mudanças de desempenho ainda são
                possíveis.
              </p>
            </section>

            <section id="avaliar-gears-beta">
              <p className="section-kicker">Teste antes de decidir</p>
              <h2>O que vale observar durante o beta</h2>
              <div className="recommendation-grid">
                <div>
                  <strong>1. Conexão e cross-play</strong>
                  <p>Veja se há atraso perceptível, desconexões e partidas desequilibradas entre plataformas no seu horário habitual.</p>
                </div>
                <div>
                  <strong>2. Sensação do combate</strong>
                  <p>Teste cobertura, movimentação, recarga ativa e precisão. Um trailer não responde se o ritmo combina com você; o beta responde.</p>
                </div>
                <div>
                  <strong>3. Desempenho real</strong>
                  <p>Observe estabilidade, nitidez e quedas de quadros no seu próprio Xbox ou PC, especialmente em Horde Siege.</p>
                </div>
                <div>
                  <strong>4. Vontade de continuar</strong>
                  <p>Depois de algumas sessões, pergunte se você voltaria toda semana. Essa resposta vale mais que qualquer promessa de catálogo.</p>
                </div>
              </div>
              <div className="note-box">
                <BadgeCheck aria-hidden="true" />
                <div>
                  <strong>Não use o beta como análise definitiva da campanha.</strong>
                  <p>
                    O teste é centrado no multiplayer. Narrativa, ritmo da campanha e conteúdo
                    final não podem ser julgados de forma responsável apenas por esta versão.
                  </p>
                </div>
              </div>
            </section>

            <section id="game-pass-gears-beta">
              <p className="section-kicker">Assinatura e lançamento</p>
              <h2>Precisa de Game Pass para jogar Gears of War: E-Day?</h2>
              <p>
                <strong>No beta público, não é necessário comprar o jogo nem fazer pré-venda.</strong>{' '}
                Em console, ainda é preciso ter acesso ao multiplayer online por um plano compatível.
                No PC, consulte a opção exibida na página oficial para sua conta e região.
              </p>
              <p>
                Para o jogo completo, a situação é diferente: a Xbox confirmou lançamento em{' '}
                <strong>6 de outubro de 2026</strong> no Xbox Series X|S, PC e nuvem, com entrada no{' '}
                <strong>Game Pass Ultimate no primeiro dia</strong>. Quem pretende jogar apenas a
                campanha pode assinar por um mês perto do lançamento; três meses só fazem sentido
                quando há interesse real no multiplayer e em outros títulos do catálogo.
              </p>
              <a className="text-link" href="/assinaturas/game-pass/">
                Ver a análise completa do Game Pass <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="faq-gears-beta">
              <h2>Perguntas frequentes sobre o beta de Gears of War: E-Day</h2>
              <div className="faq-list">
                <details>
                  <summary>O beta aberto de Gears of War: E-Day é gratuito?</summary>
                  <p>O aplicativo do beta é gratuito e não exige pré-venda neste segundo fim de semana. No Xbox, o multiplayer online requer um plano Game Pass que inclua acesso online.</p>
                </details>
                <details>
                  <summary>Até quando o beta fica disponível?</summary>
                  <p>Até 17 de agosto de 2026, às 14h no horário de Brasília, segundo a janela publicada pela Xbox.</p>
                </details>
                <details>
                  <summary>O beta está disponível no PS5?</summary>
                  <p>Não. As plataformas confirmadas para o teste são Xbox Series X|S e PC.</p>
                </details>
                <details>
                  <summary>Quais modos podem ser testados?</summary>
                  <p>Horde Siege, PvE para 12 jogadores, e Versus competitivo 4v4.</p>
                </details>
                <details>
                  <summary>O progresso do beta será levado para o jogo final?</summary>
                  <p>As fontes oficiais consultadas destacam recompensas exclusivas, mas não prometem transferência geral de progresso. Trate o beta como um teste separado.</p>
                </details>
                <details>
                  <summary>Quando Gears of War: E-Day será lançado?</summary>
                  <p>O lançamento está marcado para 6 de outubro de 2026, com disponibilidade no primeiro dia pelo Game Pass Ultimate.</p>
                </details>
              </div>
            </section>

            <section className="final-verdict">
              <p className="section-kicker">Veredito GuiaSite</p>
              <h2>Jogue o beta antes de pagar pelo jogo ou por meses de assinatura.</h2>
              <p>
                Esta é a oportunidade mais útil para descobrir se o novo multiplayer de Gears
                combina com você. Teste conexão, combate e Horde Siege no equipamento que realmente
                usa. Se a experiência convencer, um mês de Ultimate perto de 6 de outubro é a
                entrada de menor risco; assinar três meses agora sem outros jogos na fila antecipa
                um gasto que o beta ainda não justifica.
              </p>
            </section>

            <section className="affiliate-section" aria-label="Ofertas de Game Pass Ultimate">
              <div className="affiliate-heading">
                <p className="section-kicker">Para o lançamento completo</p>
                <h2>Game Pass Ultimate: compare 1 ou 3 meses</h2>
                <p>
                  O jogo completo entra no Ultimate em 6 de outubro. Não compre uma assinatura
                  apenas para baixar o beta; use estas opções somente se o plano fizer sentido para
                  seu Xbox, PC e restante do catálogo.
                </p>
              </div>
              <div className="affiliate-disclosure">
                <BadgeCheck size={18} aria-hidden="true" />
                <p>
                  Transparência editorial: os botões abaixo são links de afiliado e podem gerar
                  comissão para o GuiaSite, sem custo extra para você. Isso não altera o veredito.
                </p>
              </div>
              <div className="affiliate-grid">
                {ultimateOffers.map((offer) => (
                  <article className="affiliate-card" key={offer.href}>
                    <div className="affiliate-image">
                      <img loading="lazy" src="/guisite/game-pass-oficial.webp" alt={offer.title} />
                    </div>
                    <div className="affiliate-copy">
                      <span>{offer.badge}</span>
                      <h3>{offer.title}</h3>
                      <p>{offer.description}</p>
                      <small>Imagem oficial Xbox.</small>
                      <div className="affiliate-actions">
                        <a
                          href={offer.href}
                          target="_blank"
                          rel="nofollow sponsored noopener noreferrer"
                          aria-label={`Ver preço atual no Mercado Livre: ${offer.title}`}
                        >
                          Ver preço atual no Mercado Livre
                          <ArrowRight size={15} aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section id="fontes-gears-beta">
              <p className="section-kicker">Transparência editorial</p>
              <h2>Fontes e atualização</h2>
              <p>
                Notícia publicada em 13 de agosto de 2026. Datas, horários, plataformas, modos e
                condições de acesso foram conferidos em páginas oficiais da Xbox e de Gears of War.
                A conversão para o horário de Brasília foi calculada a partir dos horários PT
                divulgados pela Xbox.
              </p>
              <ul className="source-list">
                <li><a href={freePlayDays} target="_blank" rel="noopener noreferrer">Xbox Wire: janela pública, modos e condições do Free Play Days</a></li>
                <li><a href={xboxStoreBeta} target="_blank" rel="noopener noreferrer">Xbox Store: página oficial do beta, plataformas e recursos</a></li>
                <li><a href={xboxDirect} target="_blank" rel="noopener noreferrer">Xbox Wire: lançamento, plataformas e Game Pass Ultimate</a></li>
                <li><a href={gearsMultiplayer} target="_blank" rel="noopener noreferrer">Gears of War: detalhes oficiais do multiplayer</a></li>
              </ul>
            </section>

            <section className="related-reading">
              <p className="section-kicker">Continue decidindo melhor</p>
              <h2>Leituras relacionadas</h2>
              <div className="recommendation-grid">
                <a href="/assinaturas/game-pass/">
                  <strong>Game Pass vale a pena para quem joga pouco?</strong>
                  <p>Calcule o custo por uso antes de escolher um mês ou um trimestre.</p>
                </a>
                <a href="/games/xbox-series-s/">
                  <strong>Xbox Series S ainda vale a pena para jogador casual?</strong>
                  <p>Entenda desempenho, armazenamento e limites do console mais acessível.</p>
                </a>
                <a href="/comparar/ps-plus-vs-game-pass/">
                  <strong>PS Plus ou Game Pass: qual combina mais com você?</strong>
                  <p>Compare planos, catálogos e o valor real para sua rotina.</p>
                </a>
              </div>
            </section>
          </div>
        </div>
      </article>
      {footer}
    </main>
  );
}
